import { useEffect, useRef, useCallback } from 'react';
import { useConfirm } from '../context/ConfirmContext';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';

interface SessionStatus {
  status: 'ACTIVE' | 'IDLE' | 'UNVERIFIED' | 'EXPIRED';
  verified: boolean;
  remainingSeconds: number;
  idleSeconds: number;
  timestamp: string;
}

export const useGuestSessionMonitor = () => {
  const confirm = useConfirm();
  const { guestSession, clearGuestSession } = useAuth();
  const eventSourceRef = useRef<EventSource | null>(null);
  const warningShownRef = useRef(false);

  const connectToSessionStream = useCallback(() => {
    // SSE functionality disabled - using Socket.IO for real-time updates
    return;
    
    /* Commented out SSE code - replaced with Socket.IO
    if (!guestSession?.sessionToken || !guestSession.verified) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const url = `${baseURL}/api/guest-session/status/stream`;
      
      eventSourceRef.current = new EventSource(url, {
        withCredentials: true
      });

      eventSourceRef.current.addEventListener('session-status', (event) => {
        const status: SessionStatus = JSON.parse(event.data);
        handleSessionStatus(status);
      });

      eventSourceRef.current.addEventListener('session-expired', () => {
        handleSessionExpired();
      });

      eventSourceRef.current.onerror = (error) => {
        console.error('SSE connection error:', error);
        // Reconnect after 5 seconds
        setTimeout(connectToSessionStream, 5000);
      };
    } catch (error) {
      console.error('Failed to connect to session stream:', error);
    }
    */
  }, [guestSession]);

  const handleSessionStatus = useCallback((status: SessionStatus) => {
    // Show warning when session is about to expire (5 minutes)
    if (status.remainingSeconds < 300 && !warningShownRef.current) {
      warningShownRef.current = true;
      showExpiryWarning(status.remainingSeconds);
    }

    // Show idle warning after 20 minutes of inactivity
    if (status.idleSeconds > 1200 && status.status === 'IDLE') {
      showIdleWarning();
    }

    // Auto-refresh session if idle for less than 25 minutes
    if (status.idleSeconds > 1200 && status.idleSeconds < 1500) {
      refreshSession();
    }
  }, []);

  const handleSessionExpired = useCallback(() => {
    clearGuestSession();
    alert('Your session has expired. Please sign in again.');
    window.location.href = '/login';
  }, [clearGuestSession]);

  const refreshSession = useCallback(async () => {
    if (!guestSession?.sessionToken) return;

    try {
      await api.post('/api/guest-session/refresh');
      console.log('Session refreshed successfully');
      warningShownRef.current = false;
    } catch (error) {
      console.error('Failed to refresh session:', error);
    }
  }, [guestSession]);

  const showExpiryWarning = async (remainingSeconds: number) => {
    const minutes = Math.floor(remainingSeconds / 60);
    if (await confirm({
      title: 'Session expiring',
      description: `Your session will expire in ${minutes} minutes. Extend it?`,
      confirmText: 'Extend session',
      cancelText: 'Not now',
    })) {
      refreshSession();
    }
  };

  const showIdleWarning = async () => {
    if (await confirm({
      title: 'Still there?',
      description: 'You have been idle for a while. Keep your session active?',
      confirmText: 'Keep session',
      cancelText: 'Dismiss',
    })) {
      refreshSession();
    }
  };

  // Set up monitoring when component mounts
  useEffect(() => {
    connectToSessionStream();

    // Also check session status periodically (every 2 minutes)
    const intervalId = setInterval(async () => {
      if (guestSession?.sessionToken) {
        try {
          const response = await api.get('/api/guest-session/status');
          handleSessionStatus(response.data);
        } catch (error) {
          if (error.response?.status === 401) {
            handleSessionExpired();
          }
        }
      }
    }, 120000); // 2 minutes

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      clearInterval(intervalId);
    };
  }, [connectToSessionStream, guestSession, handleSessionStatus, handleSessionExpired]);

  return {
    refreshSession
  };
};